// Audit-trail i18n namespace (spec 16 §2.16/§4). Wired into apps/web's
// aggregated `dictionaries` (src/i18n-dictionaries.ts) so the CI key-parity gate
// (`pnpm run i18n:parity`, TESTING §12.1 / D-014) covers it automatically, and
// declared in src/i18n-completeness.ts so the VALUE gate knows which locales are
// real.
//
// `auditEn` is authoritative; `AuditDict = Translated<typeof auditEn>` makes
// ru/zh/fr parity-checked at COMPILE time. `ru` is a real translation
// (audit.ru.ts) — the person who opens this screen at KazKabel is the Big Boss,
// and an English-only compliance record is a compliance record he cannot use in
// front of an auditor. zh/fr are English placeholders, stated in
// i18n-completeness.ts rather than implied (D-122).
//
// THE WORDS THIS SCREEN HAS TO GET RIGHT, because they are what a dispute turns
// on:
//   * "audit trail" is the RECORD; "chain" is the tamper-evidence over it.
//     They are two nouns because the screen makes two different claims about
//     them, and collapsing them would let "this page's rows check out" be read
//     as "your history is intact".
//   * "checked" never means "verified everything". Every integrity sentence
//     below names its SCOPE — this page, or this range — because the API does,
//     and a UI that rounded that up would be lying on the one screen whose
//     product is trustworthiness.
//   * a withheld payload is "not shown to your role", never "empty". They are
//     different facts, the API distinguishes them, and so does this.
//
// THE MACHINE COLUMNS GET WORDS (lib/audit-labels.ts). `action`, `entity_type`
// and the actor are data written by every module, and the screen used to print
// them verbatim. `verb`, `entity` and `action` below are the words that replace
// them; a value this build has no word for reads as its area plus "changed",
// or as "Record", never as the code. The code stays on the cell's title.

import type { Translated } from "../../../i18n-shape";
import { auditRu as auditRuDict } from "./audit.ru";

export const auditEn = {
  title: "Audit trail",
  subtitle: "Every change made in this workspace, in order, sealed against tampering.",
  nav: { trail: "Audit trail" },

  filters: {
    legend: "Filter the trail",
    entityType: "Record type",
    entityId: "Record",
    entityIdPlaceholder: "Click a record in the list",
    actor: "Who",
    actorPlaceholder: "Click a name in the list",
    actionPrefix: "Area",
    actionPrefixAny: "Every area",
    from: "From",
    to: "To",
    clear: "Clear filters",
    entityIdNeedsType: "Add a record type — ids are not unique across types.",
    entityTypeAny: "Every record type",
  },

  // Area labels for the action-prefix filter. These ARE ours to name (they are
  // our module namespaces, not tenant data), unlike `entity_type`.
  area: {
    identity: "People & access",
    catalogue: "Catalogue",
    onboarding: "Set up",
    inventory: "Inventory",
    production: "Production",
    quality: "Quality",
    waste: "Waste",
    files: "Attachments",
    agent: "Agents",
    sales: "Sales",
    messaging: "Messages",
    planning: "Planning",
    other: "Other",
  },

  col: {
    seq: "#",
    when: "When",
    who: "Who",
    action: "What happened",
    entity: "Record",
    payload: "Details",
  },

  actorKind: {
    system: "System",
    anonymous: "Signed out",
    platform_staff: "Assessly support",
    unknown: "Unrecognised",
  },

  row: {
    noEntity: "—",
    payloadRedacted: "Not shown to your role",
    payloadRedactedRequires: "Needs extra access.",
    payloadUnmapped: "Not shown for this kind of entry",
    payloadHidden: "Not shown",
    payloadHiddenNeeds: "Rows marked “Not shown” need access your role does not have.",
    payloadHiddenUnmapped:
      "Rows marked “Not shown” are a kind of entry whose details are kept from every role.",
    payloadEmpty: "Nothing recorded",
    show: "Show details",
    hide: "Hide details",
  },

  // The integrity sentences. Each names its own scope, on purpose.
  chain: {
    heading: "Integrity",
    pageOk:
      "The {{rows}} entries on this page are unaltered, and the {{links}} links between them hold.",
    pageBroken: "This page does not check out — row {{seq}} ({{reason}}).",
    pageEmpty: "No rows on this page, so nothing was checked.",
    pageScopeNote:
      "This is about the rows shown here only. Filters make a page a sample, so most rows have no neighbour to link to.",
    notAnchored:
      "This page does not include the first entry, so nothing here proves where it began.",
    verifyHeading: "Whole chain",
    verifyOk: "{{length}} of {{head}} entries verified from the beginning.",
    verifyPartial: "{{length}} entries verified, ending at #{{to}} of {{head}}.",
    verifyBroken: "The chain breaks at entry #{{seq}} ({{reason}}).",
    verifyEmpty: "Nothing recorded yet.",
    verifyRunning: "Checking…",
    verifyError: "Could not check the chain.",
    reason: {
      seq_gap: "an entry is missing",
      prev_hash_mismatch: "the link to the previous entry does not match",
      curr_hash_mismatch: "the entry does not match its own fingerprint",
      unknown: "unknown reason",
    },
  },

  loading: "Loading the audit trail",
  loadMore: "Load more",
  retry: "Retry",
  requestId: "Request id",

  errorTitle: "Could not load the audit trail",
  errorBody: "The trail did not come back. Try again in a moment.",

  emptyTitle: "Nothing matches",
  emptyBody: "No entries for these filters. Widen the dates, or clear the filters.",
  emptyEntityTitle: "Nothing recorded for this record",
  emptyEntityBody:
    "The trail holds changes made through Assessly. A record created by an import or a data load has no history until someone changes it here.",

  refusedTitle: "Not your screen",
  refusedBody:
    "The audit trail is for owners and administrators. Ask your group administrator if you need it.",

  scopeRefusedTitle: "Group access needed",
  scopeRefusedBody:
    "The audit trail covers the whole company, so it needs group-wide access. Ask your group admin to widen yours.",

  offlineTitle: "You are offline",
  offlineBody: "The audit trail loads when you are back on the network.",

  entityHeading: "History of this record",
  entityBackToAll: "See the whole trail",
  // The affordance on an entity detail screen (components/audit-trail-link).
  entityLink: "Full audit trail",
  verb: {
    accepted: "accepted",
    activated: "activated",
    added: "added",
    adjusted: "adjusted",
    applied: "applied",
    approved: "approved",
    archived: "archived",
    attached: "attached",
    cancelled: "cancelled",
    changed: "changed",
    closed: "closed",
    completed: "completed",
    confirmed: "confirmed",
    connected: "connected",
    converted: "converted",
    created: "created",
    deactivated: "deactivated",
    decided: "decided",
    declined: "declined",
    deleted: "deleted",
    delivered: "delivered",
    detached: "removed",
    disabled: "turned off",
    discarded: "discarded",
    disconnected: "disconnected",
    dispatched: "sent",
    enabled: "turned on",
    ended: "ended",
    expired: "expired",
    failed: "failed",
    firmed: "firmed",
    flagged: "flagged",
    generated: "generated",
    imported: "imported",
    installed: "installed",
    invited: "invited",
    issued: "issued",
    linked: "linked",
    logged: "logged",
    lost: "lost",
    merged: "merged",
    opened: "opened",
    packed: "packed",
    picked: "picked",
    placed: "placed",
    posted: "posted",
    proposed: "proposed",
    published: "published",
    quarantined: "quarantined",
    received: "received",
    recorded: "recorded",
    registered: "registered",
    rejected: "rejected",
    released: "released",
    removed: "removed",
    renamed: "renamed",
    reopened: "reopened",
    replaced: "replaced",
    reported: "reported",
    requested: "requested",
    resolved: "resolved",
    restored: "restored",
    reversed: "reversed",
    revised: "revised",
    revoked: "revoked",
    saved: "saved",
    sent: "sent",
    shipped: "shipped",
    started: "started",
    submitted: "submitted",
    updated: "updated",
    upserted: "saved",
    verified: "verified",
    waived: "waived",
    withdrawn: "withdrawn",
    won: "won",
  },
  entity: {
    tenant: "Workspace",
    user: "User",
    facility: "Facility",
    department: "Department",
    work_center: "Work center",
    product: "Product",
    bom: "Bill of materials",
    routing: "Routing",
    catalogue_template: "Product template",
    uom: "Unit of measure",
    cost_run: "Cost run",
    attribute_schema: "Product attributes",
    import_job: "Import",
    onboarding_session: "Set-up",
    onboarding_step_state: "Set-up step",
    go_live_checklist_item: "Go-live check",
    terminology_override: "Term",
    vertical_pack_install: "Industry pack",
    qr_print_batch: "QR labels",
    stock_unit: "Stock unit",
    lot: "Lot",
    location: "Location",
    cycle_count: "Count",
    adjustment_request: "Adjustment request",
    inventory_settings: "Inventory settings",
    work_order: "Work order",
    shift_definition: "Shift",
    downtime_log: "Downtime",
    downtime_reason: "Downtime reason",
    floor_device: "Floor device",
    qc_inspection: "Inspection",
    qc_hold: "Quality hold",
    qc_control_point: "Control point",
    qc_failure_cause: "Failure cause",
    qc_cut_event: "Quality cut",
    waste_event: "Waste entry",
    waste_category: "Waste category",
    waste_cause_code: "Waste cause",
    remnant: "Remnant",
    remnant_price_class: "Remnant price class",
    waste_settings: "Waste settings",
    customer: "Customer",
    customer_contact: "Contact",
    contact_channel: "Contact channel",
    quote: "Quote",
    sales_order: "Sales order",
    sales_shipment: "Shipment",
    sales_opportunity: "Deal",
    sales_activity: "Activity",
    price_list: "Price list",
    planned_order: "Planned order",
    mps_plan: "Master schedule",
    schedule: "Schedule",
    mrp_run: "Material plan",
    forecast: "Forecast",
    conversation: "Conversation",
    message: "Message",
    message_template: "Message template",
    file: "File",
  },
  action: {
    catalogue: {
      product_created: "Product created",
      product_updated: "Product changed",
      product_activated: "Product activated",
      bom_activated: "Bill of materials put in use",
      routing_activated: "Routing put in use",
      cost_run_released: "Costs released",
    },
    identity: {
      invite_created: "Person invited",
      invite_accepted: "Invitation accepted",
      member_provisioned: "Person added",
      member_role_changed: "Role changed",
      member_revoked: "Access removed",
      facility_created: "Facility added",
      work_center_created: "Work center added",
    },
    onboarding: {
      went_live: "Factory went live",
      import_committed: "Import saved",
      pack_installed: "Industry pack installed",
      step_completed: "Set-up step completed",
      checklist_item_waived: "Go-live check waived",
    },
    inventory: {
      movement_recorded: "Stock moved",
      count_posted: "Count posted",
      transfer_dispatched: "Transfer sent",
      transfer_received: "Transfer received",
      lot_quarantined: "Lot quarantined",
      lot_released: "Lot released",
      adjustment_request_decided: "Adjustment decided",
    },
    production: {
      work_order_created: "Work order created",
      work_order_released: "Work order released",
      work_order_completed: "Work order completed",
      work_order_closed: "Work order closed",
      operation_started: "Operation started",
      operation_completed: "Operation completed",
      qty_reported: "Output reported",
      downtime_logged: "Downtime logged",
      shift_closed: "Shift closed",
      cost_adjusted: "Cost adjusted",
    },
    quality: {
      inspection_completed: "Inspection completed",
      inspection_result_recorded: "Inspection result recorded",
      hold_placed: "Hold placed",
      hold_released: "Hold released",
    },
    waste: {
      event_recorded: "Waste recorded",
      remnant_registered: "Remnant registered",
    },
    sales: {
      customer_created: "Customer created",
      customer_updated: "Customer changed",
    },
    sales_quote: {
      created: "Quote created",
      sent: "Quote sent",
      revised: "Quote revised",
    },
    sales_order: {
      created: "Sales order created",
      cancelled: "Sales order cancelled",
    },
    sales_shipment: {
      shipped: "Shipment sent",
    },
    sales_opportunity: {
      won: "Deal won",
      lost: "Deal lost",
    },
    planned_order: {
      firmed: "Planned order firmed",
      converted: "Planned order made a work order",
    },
    schedule: {
      published: "Schedule published",
    },
    mps_plan: {
      approved: "Master schedule approved",
    },
  },
  actionPhrase: "{{area}}: {{verb}}",
  actionOther: "{{area}}: changed",
  entityOther: "Record",
} as const;

// The dictionary SHAPE, with values widened to `string` (src/i18n-shape.ts).
export type AuditDict = Translated<typeof auditEn>;

// ru is TRANSLATED (audit.ru.ts). fr, zh, zh-Hant and hi are machine-translated
// in audit.<locale>.ts, which src/locales/<locale>.ts imports directly. The
// claims live in src/i18n-completeness.ts and tools/ci/i18n-parity.ts holds
// every locale to them.
export const auditRu: AuditDict = auditRuDict;
